import React, { Suspense, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import AppLayout from '../components/layouts/AppLayout';
import UnauthenticatedInvitePage from '../pages/unauthenticated/UnauthenticatedInvitePage';
import UnauthenticatedBusinessInvitePage from '../pages/unauthenticated/UnauthenticatedCommunityInvitePage';
import LoginPage from '../pages/unauthenticated/LoginPage';
import AuthenticatedBusinessInvitePage from '../pages/authenticated/AuthenticatedCommunityInvitePage';
import AuthenticatedInvitePage from '../pages/authenticated/AuthenticatedInvitePage';
import SuccessPage from '../pages/common/SuccessPage';
import PaidPage from '../pages/common/PaidPage';
import NotFoundPage from '../pages/common/NotFoundPage';
import RedirectPage from '../pages/common/RedirectPage';
import { detectMobileOS } from '../utils/userAgentUtils';
import { MobileOS } from '../types/misc';
import UnauthenticatedLocalContactInvitePage from '../pages/unauthenticated/UnauthenticatedLocalContactInvitePage';
import AuthenticatedLocalContactInvitePage from '../pages/authenticated/AuthenticatedLocalContactInvitePage';
import UnauthenticatedPersonalInvitePage from '../pages/unauthenticated/UnauthenticatedPersonalInvitePage';
import AuthenticatedPersonalInvitePage from '../pages/authenticated/AuthenticatedPersonalInvitePage';
import InviteRedirectPage from '../pages/common/InviteRedirectPage';
import AuthenticatedPersonalLinkPage from '../pages/authenticated/AuthenticatedPersonalLinkPage';
import UnauthenticatedPersonalLinkPage from '../pages/unauthenticated/UnauthenticatedPersonalLinkPage';
import DataRequestPage from '../pages/common/app/DataRequestPage';
import IntroductionsPage from '../pages/common/app/IntroductionsPage';
import EmailPreferencesPage from '../pages/common/app/EmailPreferencesPage';
import ConnectionInvitePage from '../pages/common/app/ConnectionInvitePage';
import SharedWithCommunityMembersPage from '../pages/authenticated/SharedWithCommunityMembersPage';
import EventRespondPage from '../pages/common/app/EventRespondPage';
import DatePickerRespondPage from '../pages/common/app/DatePickerRespondPage';
import AuthenticatedEventRespondPage from '../pages/authenticated/AuthenticatedEventRespondPage';
import AuthenticatedDatePickerRespondPage from '../pages/authenticated/AuthenticatedDatePickerRespondPage';
import UnauthenticatedEventRespondPage from '../pages/unauthenticated/UnauthenticatedEventRespondPage';
import UnauthenticatedDatePickerRespondPage from '../pages/unauthenticated/UnauthenticatedDatePickerRespondPage';
import MagicLoginPage from '../pages/common/MagicLoginPage';
import { redirectComWebsite } from '../utils/appUtils';
import UnauthenticatedCrmUserInvitePage from '../pages/unauthenticated/UnauthenticatedCrmUserInvitePage';
import AuthenticatedCrmUserInvitePage from '../pages/authenticated/AuthenticatedCrmUserInvitePage';
import DownloadPage from '../pages/common/DownloadPage';
import ConnectionsListPage from '../pages/authenticated/app/connections/ConnectionsPage';
import HomePage from '../pages/authenticated/app/HomePage';
import InboxPage from '../pages/authenticated/app/InboxPage';
import CommunitiesListPage from '../pages/authenticated/app/communities/CommunitiesListPage';
import CommunityLayout from '../pages/authenticated/app/communities/CommunityLayout';
import CommunityOverviewPage from '../pages/authenticated/app/communities/CommunityOverviewPage';
import CommunityEventsPage from '../pages/authenticated/app/communities/CommunityEventsPage';
import CommunityAlbumsPage from '../pages/authenticated/app/communities/CommunityAlbumsPage';
import CommunityAlbumDetailPage from '../pages/authenticated/app/communities/CommunityAlbumDetailsPage';
import CommunityTasksPage from '../pages/authenticated/app/communities/CommunityTasksPage';
import CommunityScoresPage from '../pages/authenticated/app/communities/CommunityScoresPage';
import SponsorOverviewPage from '../pages/authenticated/app/communities/SponsorOverviewPage';
import CommunityFormsPage from '../pages/authenticated/app/communities/CommunityFormsPage';
import CommunityFormDetailPage from '../pages/authenticated/app/communities/CommunityFormDetailPage';
import CommunityFormResultsPage from '../pages/authenticated/app/communities/CommunityFormResultsPage';
import CreateFormPage from '../pages/authenticated/app/communities/CreateFormPage';
import EventsListPage from '../pages/authenticated/app/events/AgendaPage';
import ProfilePage from '../pages/authenticated/app/profile/ProfilePage';
import SettingsPage from '../pages/authenticated/app/profile/SettingsPage';
import FeedbackPage from '../pages/authenticated/app/FeedbackPage';
import ChatsPage from '../pages/authenticated/app/ChatsPage';
import CommunityFinancesPage from '../pages/authenticated/app/communities/CommunityFinancesPage';

interface RouteConfig {
  path: string;
  element: JSX.Element;
  children?: Array<{ index?: boolean; path?: string; element: JSX.Element }>;
}

function Router(): JSX.Element {
  const auth = useAuth();
  const mobileOS = detectMobileOS();
  const location = useLocation();

  useLayoutEffect(() => {
    if (mobileOS === MobileOS.ANDROID) redirectToApp();
    redirectToComWebsite();
  }, [location.pathname]);

  const redirectToApp = async () => {
    const url = window.location.pathname + window.location.search;
    if (window.location.pathname === '/redirect') return;
    const link = `vera:/${url}`;
    try {
      window.location.replace(link);
    } catch (err) {
      console.error('Error redirecting to app:', err);
      window.location.replace(url);
    }
  };

  const redirectToComWebsite = async () => {
    switch (window.location.pathname) {
      case '/about':
        redirectComWebsite('/about');
        break;
      case '/account':
        redirectComWebsite('/account');
        break;
      case '/privacy-statement':
        redirectComWebsite('/privacy-statement');
        break;
      default:
        break;
    }
  };

  return (
    <Routes>
      {auth ? (
        <>
          <Route element={<AppLayout contentWrapper={false} />}>
            <Route path="/connections" element={<ConnectionsListPage />} />
          </Route>
          <Route element={<AppLayout />}>
            {authLayoutRoutes.map((route: RouteConfig) => {
              if (route.children) {
                return (
                  <Route key={route.path} path={route.path} element={route.element}>
                    {route.children.map((child: any) =>
                      child.index ? (
                        <Route key={`${route.path}-index`} index element={child.element} />
                      ) : (
                        <Route key={`${route.path}-${child.path}`} path={child.path} element={child.element} />
                      ),
                    )}
                  </Route>
                );
              }
              return <Route key={route.path} path={route.path} element={route.element} />;
            })}
          </Route>

          {authNoLayoutRoutes.map((route: RouteConfig) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
        </>
      ) : (
        <>
          {noAuthRoutes.map((route: RouteConfig) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
        </>
      )}
      <Route path="/magic" element={<MagicLoginPage />} />
      <Route path="/vc/:linkId" element={<InviteRedirectPage />} />
      <Route path="/data-request" element={<DataRequestPage />} />
      {/* Internal in-app event response view (not used for external invite links) */}
      <Route path="/event" element={<EventRespondPage />} />
      {/* Internal in-app date-picker response view (not used for external invite links) */}
      <Route path="/date-picker" element={<DatePickerRespondPage />} />
      <Route path="/email-preferences" element={<EmailPreferencesPage />} />
      <Route path="/communities/:communityId/members" element={<SharedWithCommunityMembersPage />} />
      <Route path="/introductions" element={<IntroductionsPage />} />
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/paid" element={<PaidPage />} />
      <Route path="/redirect" element={<RedirectPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/connectionInvite" element={<ConnectionInvitePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function WrapperRouter(): JSX.Element {
  return (
    <Suspense fallback="">
      <Router />
    </Suspense>
  );
}

const authLayoutRoutes = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/communities',
    element: <CommunitiesListPage />,
  },
  {
    path: '/community',
    element: <CommunityLayout />,
    children: [
      { index: true, element: <CommunityOverviewPage /> },
      { path: 'sponsors/:sponsorId', element: <SponsorOverviewPage /> },
      { path: 'events', element: <CommunityEventsPage /> },
      { path: 'albums', element: <CommunityAlbumsPage /> },
      { path: 'albums/:albumId', element: <CommunityAlbumDetailPage /> },
      { path: 'tasks', element: <CommunityTasksPage /> },
      { path: 'scores', element: <CommunityScoresPage /> },
      { path: 'finances', element: <CommunityFinancesPage /> },
      { path: 'forms', element: <CommunityFormsPage /> },
      { path: 'forms/create', element: <CreateFormPage /> },
      { path: 'forms/:formSlug', element: <CommunityFormDetailPage /> },
      { path: 'forms/:formSlug/edit', element: <CreateFormPage /> },
      { path: 'forms/:formSlug/results', element: <CommunityFormResultsPage /> },
    ],
  },
  {
    path: '/agenda',
    element: <EventsListPage />,
  },
  {
    path: '/inbox',
    element: <InboxPage />,
  },
  {
    path: '/chats',
    element: <ChatsPage />,
  },
  {
    path: '/profile',
    element: <ProfilePage />,
  },
  {
    path: '/settings',
    element: <SettingsPage />,
  },
  {
    path: '/feedback',
    element: <FeedbackPage />,
  },
];

const authNoLayoutRoutes = [
  {
    path: '/invite',
    element: <AuthenticatedInvitePage />,
  },
  {
    path: '/moments/invite',
    element: <AuthenticatedBusinessInvitePage />,
  },
  {
    path: '/moments/local-invite',
    element: <AuthenticatedLocalContactInvitePage />,
  },
  {
    path: '/personal/invite',
    element: <AuthenticatedPersonalInvitePage />,
  },
  {
    path: '/personalLink',
    element: <AuthenticatedPersonalLinkPage />,
  },
  {
    path: '/event/invite',
    // External event invite entry point – routed based on auth state
    element: <AuthenticatedEventRespondPage />,
  },
  {
    path: '/date-picker/invite',
    // External date-picker invite entry point – routed based on auth state
    element: <AuthenticatedDatePickerRespondPage />,
  },
  {
    path: '/crm/invite',
    element: <AuthenticatedCrmUserInvitePage />,
  },
];

const noAuthRoutes = [
  {
    path: '/',
    element: <Navigate to="/login" />,
  },
  {
    path: '/invite',
    element: <UnauthenticatedInvitePage />,
  },
  {
    path: '/moments/invite',
    element: <UnauthenticatedBusinessInvitePage />,
  },
  {
    path: '/moments/local-invite',
    element: <UnauthenticatedLocalContactInvitePage />,
  },
  {
    path: '/personal/invite',
    element: <UnauthenticatedPersonalInvitePage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/personalLink',
    element: <UnauthenticatedPersonalLinkPage />,
  },
  {
    path: '/event/invite',
    // External event invite entry point – routed based on auth state
    element: <UnauthenticatedEventRespondPage />,
  },
  {
    path: '/date-picker/invite',
    // External date-picker invite entry point – routed based on auth state
    element: <UnauthenticatedDatePickerRespondPage />,
  },
  {
    path: '/crm/invite',
    element: <UnauthenticatedCrmUserInvitePage />,
  },
];
